import React, { useEffect, useMemo, useRef } from 'react';
import '../../RelatedProductsSlider.less';
import { initSlider } from '../../RelatedProductsSlider.js';
import ProductSliderCard from './ProductSliderCard.tsx';
import { ProductSliderItem } from './ProductSliderItem.ts';
import { ProductSliderOptions, defaultProductSliderOptions } from './ProductSliderOptions.ts';

export type ProductSliderProps = {
  title?: string;
  description?: string;
  products: ProductSliderItem[];
  options?: ProductSliderOptions;
  favoriteIds?: string[];
};

const getFavoriteIdsFromCookie = (): string[] => {
  const match = document.cookie.split('; ').find((row) => row.startsWith('ProductFavorites='));
  if (!match) return [];

  try {
    return JSON.parse(decodeURIComponent(match.split('=').slice(1).join('=')));
  } catch {
    return [];
  }
};

const ProductSlider = ({ title, description, products, options, favoriteIds }: ProductSliderProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const mergedOptions = { ...defaultProductSliderOptions, ...options };
  const hasHeading = Boolean(title);
  const productIdsKey = useMemo(() => products.map((p) => p.productId).join(','), [products]);
  const resolvedFavoriteIds = favoriteIds ?? getFavoriteIdsFromCookie();

  useEffect(() => {
    if (containerRef.current && products.length > 0) {
      initSlider(containerRef.current);
    }
  }, [productIdsKey, products.length]);

  if (!products.length) {
    return null;
  }

  return (
    <div key={productIdsKey} ref={containerRef} className="accessory-promotion-container react-product-slider mb-12">
      <div className="top-section-wrapper">
        <div className={`top-section ${hasHeading ? '' : 'no-heading'}`}>
          {hasHeading && (
            <div>
              <h3 className="heading3 mb-0">{title}</h3>
              {description && <p className="paragraph mb-0">{description}</p>}
            </div>
          )}
          <div className="d-none d-lg-flex arrow-container">
            <div className="arrow me-4">
              <i className="le-icon-chevron" />
            </div>
            <div className="arrow arrow-right">
              <i className="le-icon-chevron rot-180" />
            </div>
          </div>
        </div>
      </div>
      <div className="promotion scrollBar--3">
        {products.map((product, index) => (
          <ProductSliderCard
            key={product.productId}
            product={product}
            position={index + 1}
            options={mergedOptions}
            favoriteIds={resolvedFavoriteIds}
          />
        ))}
      </div>
    </div>
  );
};

export default ProductSlider;
